import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Share2, CheckCircle, X, Car, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

// Mock affiliated workshops
const MOCK_TALLERES = [
  { id: 1, nombre: 'Taller Gearcontrol Norte', direccion: 'Av. Insurgentes Norte 1200, CDMX', lat: 19.4978, lng: -99.1269, especialidad: 'Mecánica general' },
  { id: 2, nombre: 'Taller Gearcontrol Sur', direccion: 'Calz. de Tlalpan 3800, CDMX', lat: 19.3284, lng: -99.1527, especialidad: 'Diagnóstico electrónico' },
  { id: 3, nombre: 'Taller Gearcontrol Oriente', direccion: 'Av. Zaragoza 450, CDMX', lat: 19.4195, lng: -99.0645, especialidad: 'Frenos y suspensión' },
  { id: 4, nombre: 'Taller Gearcontrol Poniente', direccion: 'Blvd. Adolfo López Mateos 1800, CDMX', lat: 19.3892, lng: -99.2341, especialidad: 'Transmisiones' },
  { id: 5, nombre: 'Taller Gearcontrol Centro', direccion: 'Eje Central Lázaro Cárdenas 800, CDMX', lat: 19.4326, lng: -99.1332, especialidad: 'Servicio express' },
];

const buildHistorialSnapshot = (user, vehiculo) => ({
  cliente: user?.email || 'Cliente',
  vehiculo: vehiculo
    ? `${vehiculo?.year || vehiculo?.anio || ''} ${vehiculo?.make || vehiculo?.marca || ''} ${vehiculo?.model || vehiculo?.modelo || ''}`?.trim()
    : 'Sin vehículo seleccionado',
  placa: vehiculo?.plate || vehiculo?.license_plate || vehiculo?.placa || '—',
  vin: vehiculo?.vin || '—',
  compartido_en: new Date()?.toISOString(),
});

const vehicleLabel = (v) => {
  if (!v) return '—';
  const year = v?.year || v?.anio || '';
  const make = v?.make || v?.marca || '';
  const model = v?.model || v?.modelo || '';
  const plate = v?.plate || v?.license_plate || v?.placa || '';
  const label = `${year} ${make} ${model}`?.trim();
  return plate ? `${label} (${plate})` : label || v?.id;
};

// Leaflet loaded via CDN to avoid SSR/build issues
const LeafletMap = ({ talleres, onSelectTaller, selectedId }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (!window.L) return;
    if (mapInstanceRef?.current) return;

    const L = window.L;
    const map = L?.map(mapRef?.current)?.setView([19.4326, -99.1332], 11);
    L?.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
    })?.addTo(map);
    mapInstanceRef.current = map;

    talleres?.forEach((t) => {
      const marker = L?.marker([t?.lat, t?.lng])?.addTo(map)?.bindPopup(`<b>${t?.nombre}</b><br/>${t?.direccion}`);
      marker?.on('click', () => onSelectTaller(t));
      markersRef?.current?.push({ id: t?.id, marker });
    });

    return () => {
      map?.remove();
      mapInstanceRef.current = null;
      markersRef.current = [];
    };
  }, []);

  return <div ref={mapRef} style={{ height: '100%', width: '100%', borderRadius: '8px' }} />;
};

const RedGearcontrol = () => {
  const { user, userProfile } = useAuth();
  const [leafletReady, setLeafletReady] = useState(!!window.L);
  const [selectedTaller, setSelectedTaller] = useState(null);
  const [shareStatus, setShareStatus] = useState(null); // null | 'confirming' | 'sent'

  // Real vehicle state
  const [vehicles, setVehicles] = useState([]);
  const [vehiculoActual, setVehiculoActual] = useState(null);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);

  // Load Leaflet CSS + JS if not already loaded
  useEffect(() => {
    if (window.L) { setLeafletReady(true); return; }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head?.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => setLeafletReady(true);
    document.head?.appendChild(script);
  }, []);

  // Fetch real vehicles from Supabase using authenticated company_id
  useEffect(() => {
    const companyId = userProfile?.company_id;
    if (!companyId) return;

    const fetchVehicles = async () => {
      setVehiclesLoading(true);
      try {
        const { data, error } = await supabase
          ?.from('vehicles')
          ?.select('id, make, model, year, vin, plate, license_plate, marca, modelo, anio, placa, client_id')
          ?.eq('company_id', companyId)
          ?.order('created_at', { ascending: false })
          ?.limit(50);

        if (!error && data?.length > 0) {
          setVehicles(data);
          setVehiculoActual(data?.[0]);
        } else {
          setVehicles([]);
          setVehiculoActual(null);
        }
      } catch {
        setVehicles([]);
        setVehiculoActual(null);
      } finally {
        setVehiclesLoading(false);
      }
    };

    fetchVehicles();
  }, [userProfile?.company_id]);

  const handleCompartir = () => setShareStatus('confirming');

  const handleConfirmarCompartir = () => {
    const snapshot = buildHistorialSnapshot(user, vehiculoActual);
    // TODO: replace with real API call when backend is ready
    console.log('[RedGearcontrol] Historial compartido con taller:', selectedTaller?.id, snapshot);
    setShareStatus('sent');
  };

  const handleCerrarModal = () => {
    setShareStatus(null);
    if (shareStatus === 'sent') setSelectedTaller(null);
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MapPin className="text-blue-600" size={22} />
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Red Gearcontrol</h1>
            <p className="text-xs text-gray-500">Talleres afiliados — selecciona uno para compartir historial</p>
          </div>
        </div>

        {/* Vehicle selector */}
        <div className="relative">
          <button
            onClick={() => setShowVehicleDropdown(!showVehicleDropdown)}
            className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors min-w-[200px] max-w-[280px]"
          >
            <Car size={15} className="text-blue-500 flex-shrink-0" />
            <span className="flex-1 text-left truncate">
              {vehiclesLoading
                ? 'Cargando vehículos…'
                : vehiculoActual
                  ? vehicleLabel(vehiculoActual)
                  : 'Sin vehículo'}
            </span>
            <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
          </button>

          {showVehicleDropdown && vehicles?.length > 0 && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 w-72 max-h-60 overflow-y-auto">
              {vehicles?.map((v) => (
                <button
                  key={v?.id}
                  onClick={() => { setVehiculoActual(v); setShowVehicleDropdown(false); setShareStatus(null); }}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0 ${vehiculoActual?.id === v?.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                >
                  {vehicleLabel(v)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar: taller list */}
        <div className="w-72 bg-white border-r border-gray-200 overflow-y-auto flex-shrink-0">
          <div className="p-3 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Talleres afiliados ({MOCK_TALLERES?.length})</span>
          </div>
          {MOCK_TALLERES?.map((t) => (
            <button
              key={t?.id}
              onClick={() => { setSelectedTaller(t); setShareStatus(null); }}
              className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-blue-50 transition-colors ${selectedTaller?.id === t?.id ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''}`}
            >
              <p className="text-sm font-medium text-gray-800">{t?.nombre}</p>
              <p className="text-xs text-gray-500 mt-0.5">{t?.especialidad}</p>
              <p className="text-xs text-gray-400 mt-0.5">{t?.direccion}</p>
            </button>
          ))}
        </div>

        {/* Map area */}
        <div className="flex-1 flex flex-col relative" onClick={() => setShowVehicleDropdown(false)}>
          <div className="flex-1 p-4">
            {leafletReady ? (
              <LeafletMap
                talleres={MOCK_TALLERES}
                onSelectTaller={(t) => { setSelectedTaller(t); setShareStatus(null); }}
                selectedId={selectedTaller?.id}
              />
            ) : (
              <div className="h-full flex items-center justify-center bg-gray-100 rounded-lg">
                <p className="text-sm text-gray-500">Cargando mapa…</p>
              </div>
            )}
          </div>

          {/* Selected taller action bar */}
          {selectedTaller && shareStatus !== 'sent' && (
            <div className="bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-800">{selectedTaller?.nombre}</p>
                <p className="text-xs text-gray-500">{selectedTaller?.direccion}</p>
              </div>
              <button
                onClick={handleCompartir}
                disabled={!vehiculoActual}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
              >
                <Share2 size={15} />
                Compartir historial del vehículo
              </button>
            </div>
          )}

          {shareStatus === 'sent' && (
            <div className="bg-green-50 border-t border-green-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle size={18} />
                <span className="text-sm font-medium">Historial compartido con {selectedTaller?.nombre}</span>
              </div>
              <button onClick={handleCerrarModal} className="text-xs text-gray-500 hover:text-gray-700 underline">Cerrar</button>
            </div>
          )}
        </div>
      </div>

      {/* Confirm modal */}
      {shareStatus === 'confirming' && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">Confirmar compartir historial</h2>
              <button onClick={handleCerrarModal} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Car size={15} className="text-blue-500" />
                <span className="font-medium">Vehículo:</span>
                <span className="truncate">{vehiculoActual ? vehicleLabel(vehiculoActual) : '—'}</span>
              </div>
              {(vehiculoActual?.vin) && (
                <p className="text-xs text-gray-500 pl-5">VIN: {vehiculoActual?.vin}</p>
              )}
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <MapPin size={15} className="text-blue-500" />
                <span className="font-medium">Taller destino:</span>
                <span>{selectedTaller?.nombre}</span>
              </div>
            </div>

            <p className="text-xs text-gray-500 mb-5">
              Se compartirá el historial de servicios del vehículo seleccionado con este taller afiliado. Solo podrán ver el historial de servicios registrados en Gearcontrol.
            </p>

            <div className="flex gap-3">
              <button onClick={handleCerrarModal} className="flex-1 border border-gray-300 text-gray-700 text-sm font-medium py-2 rounded-lg hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button onClick={handleConfirmarCompartir} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded-lg transition-colors">
                Confirmar y compartir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RedGearcontrol;
